import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Suspense, lazy, type ReactNode } from 'react'

import { useAuth } from '@/lib/auth'
import { useExternalLinkGuard } from '@/lib/externalLinks'
import { TenantGate } from '@/lib/tenantGate'
import ErrorBoundary from '@/components/ErrorBoundary'

import LoginPage from '@/features/auth/LoginPage'
import DashboardPage from '@/features/dashboard/DashboardPage'
import { FullPageLoader } from '@/components/Loader'
import { ViewModeProvider } from '@/lib/viewMode'


/**
 * Every screen but the two first ones is its own chunk.
 *
 * The application shipped as a single 750 KB bundle, so signing in downloaded
 * the briefings wizard, the analytics charts and every settings page before the
 * dashboard could paint. LoginPage and DashboardPage stay eager because they
 * ARE the first paint - lazy-loading those would trade bytes for a spinner in
 * the one place a spinner is most visible.
 */
const SignupPage = lazy(() => import('@/features/auth/SignupPage'))
const SourcePage = lazy(() => import('@/features/onboarding/SourcePage'))
const SyncPage = lazy(() => import('@/features/onboarding/SyncPage'))
const ReviewPage = lazy(() => import('@/features/onboarding/ReviewPage'))
const UploadFilesPage = lazy(() => import('@/features/onboarding/UploadFilesPage'))
const PasSetupPage = lazy(() => import('@/features/onboarding/PasSetupPage'))
const OtherSourcesPage = lazy(() => import('@/features/onboarding/OtherSourcesPage'))
const SourceWizardPage = lazy(() => import('@/features/onboarding/SourceWizardPage'))
const DataSourcesPage = lazy(() => import('@/features/dataSources/DataSourcesPage'))
const SyncHistoryPage = lazy(() => import('@/features/sources/SyncHistoryPage'))
const AlertsPage = lazy(() => import('@/features/alerts/AlertsPage'))
const CircularFeedPage = lazy(() => import('@/features/circulars/CircularFeedPage'))
const ChangeResponsePage = lazy(() => import('@/features/changeResponse/ChangeResponsePage'))
const BriefingsPage = lazy(() => import('@/features/briefings/BriefingsPage'))
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'))
const AlertRoutingPage = lazy(() => import('@/features/alertRouting/AlertRoutingPage'))
const CarrierProfilePage = lazy(() => import('@/features/carrierProfile/CarrierProfilePage'))
const ConnectionsPage = lazy(() => import('@/features/connections/ConnectionsPage'))
const SearchPage = lazy(() => import('@/features/search/SearchPage'))
const AcceptInvitePage = lazy(() => import('@/features/auth/AcceptInvitePage'))
const ProfilePage = lazy(() => import('@/features/settings/ProfilePage'))
const NotificationPrefsPage = lazy(() => import('@/features/settings/NotificationPrefsPage'))
const UsersPage = lazy(() => import('@/features/settings/UsersPage'))
const RolesPage = lazy(() => import('@/features/settings/RolesPage'))
const SsoPage = lazy(() => import('@/features/settings/SsoPage'))
const CompanySettingsPage = lazy(() => import('@/features/settings/CompanySettingsPage'))

function FullPageSpinner() {
  return <FullPageLoader label="Loading Insuronix" />
}


/** Signed in? Onboarded? Otherwise bounce to the right place. */
function Protected({ children, requireOnboarded = true }: { children: ReactNode; requireOnboarded?: boolean }) {
  const { me, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner />
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  // A temporary password must be changed before anything else. The API refuses
  // this account's writes until then; sending them to the password screen makes
  // that a guided step rather than a wall of 403s.
  if (me.mustChangePassword && location.pathname !== '/settings/profile') {
    return <Navigate to="/settings/profile" replace />
  }
  // **And it outranks onboarding**, or the two guards send each other in a
  // circle until the browser gives up and renders nothing.
  //
  // Each is right alone. Together they trap anybody who is both: the password
  // rule sends /onboarding/source to /settings/profile, and the onboarding
  // rule sends /settings/profile back to /onboarding/source. Chrome stops it
  // with "Throttling navigation to prevent the browser from hanging" and the
  // page comes out blank, with no error anywhere - it is not a crash, it is a
  // loop.
  //
  // Being both was unreachable until the admin portal existed, and is now the
  // state of *every* carrier it creates: a first administrator with a handover
  // password, in an organization that has onboarded nothing. The first person
  // to sign in to Nallas saw a white screen.
  if (requireOnboarded && !me.onboardingCompleted && !me.mustChangePassword) {
    return <Navigate to="/onboarding/source" replace />
  }
  return <>{children}</>
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (me) return <Navigate to={me.onboardingCompleted ? '/dashboard' : '/onboarding/source'} replace />
  // Only the signed-out pages need this. Somebody with a session already has
  // a carrier, and the cookie is __Host-, so it could not have come from a
  // host that belongs to nobody.
  return <TenantGate>{children}</TenantGate>
}

export default function App() {
  // Any link leaving Insuronix opens in a new tab, including links rendered
  // from seeded data that no component here knows about.
  useExternalLinkGuard()

  return (
    <ViewModeProvider>
    {/* One screen failing to render must not take the application with it. */}
    <ErrorBoundary>
    <Suspense fallback={<FullPageSpinner />}>
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><SignupPage /></PublicOnly>} />

      {/* The "What do you write?" step is gone: what the carrier writes comes from
          their own data, not a form filled in before any of it has been read. */}
      <Route path="/onboarding/basics" element={<Navigate to="/onboarding/source" replace />} />
      <Route path="/onboarding/source" element={<Protected requireOnboarded={false}><SourcePage /></Protected>} />
      {/* The single-file and request-form screens these replaced. */}
      <Route path="/onboarding/connect/:source" element={<Navigate to="/onboarding/source" replace />} />
      <Route path="/onboarding/sync/:source" element={<Protected requireOnboarded={false}><SyncPage /></Protected>} />
      <Route path="/onboarding/review/:source" element={<Protected requireOnboarded={false}><ReviewPage /></Protected>} />
      {/* Source setup is reachable before and after onboarding: the same wizards add sources later. */}
      <Route path="/onboarding/upload" element={<Protected requireOnboarded={false}><UploadFilesPage /></Protected>} />
      <Route path="/onboarding/pas" element={<Protected requireOnboarded={false}><PasSetupPage /></Protected>} />
      <Route path="/onboarding/sources" element={<Protected requireOnboarded={false}><OtherSourcesPage /></Protected>} />
      <Route path="/onboarding/sources/:kind" element={<Protected requireOnboarded={false}><SourceWizardPage /></Protected>} />
      <Route path="/onboarding/pas-request/*" element={<Navigate to="/onboarding/pas" replace />} />

      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/alerts" element={<Protected><AlertsPage /></Protected>} />
      <Route path="/circular-feed" element={<Protected><CircularFeedPage /></Protected>} />
      <Route path="/change-response" element={<Protected><ChangeResponsePage /></Protected>} />
      <Route path="/change-response/:changeId" element={<Protected><ChangeResponsePage /></Protected>} />
      <Route path="/briefings" element={<Protected><BriefingsPage /></Protected>} />
      <Route path="/analytics" element={<Protected><AnalyticsPage /></Protected>} />
      <Route path="/alert-routing" element={<Protected><AlertRoutingPage /></Protected>} />
      <Route path="/carrier-profile" element={<Protected><CarrierProfilePage /></Protected>} />
      <Route path="/connections" element={<Protected><ConnectionsPage /></Protected>} />
      <Route path="/data-sources" element={<Protected><DataSourcesPage /></Protected>} />
      {/* The feed's own call history. Its own page: it answers "where did this
          circular come from", which is not a data-source question. */}
      <Route path="/sync-history" element={<Protected><SyncHistoryPage /></Protected>} />
      <Route path="/search" element={<Protected><SearchPage /></Protected>} />

      <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
      <Route path="/settings/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/settings/notifications" element={<Protected><NotificationPrefsPage /></Protected>} />
      {/* Non-admins get an "administrator access required" state, not a redirect. */}
      <Route path="/settings/users" element={<Protected><UsersPage /></Protected>} />
      <Route path="/settings/roles" element={<Protected><RolesPage /></Protected>} />
      <Route path="/settings/company" element={<Protected><CompanySettingsPage /></Protected>} />
      <Route path="/settings/sso" element={<Protected><SsoPage /></Protected>} />

      {/* Public: an invitee has no session yet, so this route is unguarded. */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
    </ViewModeProvider>
  )
}
